import React from 'react'
import Typography from '@mui/material/Typography'
import TableSortLabel from '@mui/material/TableSortLabel'

import { usePageParams } from '../../utils/usePageParams'

export enum SortDirection {
    asc = 'asc',
    desc = 'desc',
}

const classes = {
    columnHeader: {
        fontWeight: 'bold',
        textTransform: 'capitalize',
    },
}

/**
 * Returns reverse direction or 'asc' as default
 * @param direction - current direction
 */
const getNewDirection = (direction?: SortDirection) => {
    if (!direction) return SortDirection.asc
    return direction === SortDirection.asc ? SortDirection.desc : SortDirection.asc
}

type SortOrder<T extends string> = { asc?: T } | { desc?: T }

export interface ColumnHeaderProps<TSort extends string = string> {
    hidden?: boolean
    byParameter: TSort
}
/**
 * Column header for sortable column (only for Redux parameters based tables)
 * @param byParameter - which sort parameter we should set by clicking on it
 * @param title - column title
 */
const UrlParamsSortableHeader = ({ title, sortKey }: { title: React.ReactNode | string; sortKey: string }) => {
    const [pageParams, setPageParams] = usePageParams<SortOrder<string>>()
    return (
        <SortableHeader
            title={title}
            sortKey={sortKey}
            currentSortKey={pageParams}
            onSortOrderChanged={setPageParams}
        />
    )
}

const SortableHeader = <TSort extends string>({
    title,
    sortKey,
    currentSortKey,
    onSortOrderChanged,
}: {
    title: React.ReactNode | string
    sortKey: TSort
    onSortOrderChanged: (value: SortOrder<TSort>) => void
    currentSortKey?: SortOrder<string>
}) => {
    let sortProps: {
        active?: boolean
        direction?: SortDirection
    } = {}

    if (currentSortKey && 'asc' in currentSortKey && currentSortKey.asc === sortKey) {
        sortProps = {
            active: true,
            direction: SortDirection.asc,
        }
    } else if (currentSortKey && 'desc' in currentSortKey && currentSortKey.desc === sortKey) {
        sortProps = {
            active: true,
            direction: SortDirection.desc,
        }
    }
    const setSort = (parameter: TSort, direction: SortDirection) => {
        const newSorting = {
            asc: direction === SortDirection.asc ? parameter : undefined,
            desc: direction === SortDirection.desc ? parameter : undefined,
        }
        onSortOrderChanged(newSorting)
    }

    const onClick = () => setSort(sortKey, getNewDirection(sortProps.direction))

    return (
        <TableSortLabel {...sortProps} onClick={onClick}>
            <Typography variant="body2" sx={classes.columnHeader}>
                {title}
            </Typography>
        </TableSortLabel>
    )
}
/**
 * Common table column header
 * @param props - title and sorting properties
 */
const ColumnHeader = <TSort extends string>({
    title,
    sorting,
    currentSortKey,
    onSortOrderChanged,
}: {
    title: React.ReactNode | string
    sorting?: {
        hidden?: boolean
        byParameter: TSort
    }
    currentSortKey?: SortOrder<string>
    onSortOrderChanged?: (sortOrder: { asc?: TSort } | { desc?: TSort }) => void
}) => {
    return !sorting || sorting.hidden ? (
        <Typography variant="body2" sx={classes.columnHeader}>
            {title}
        </Typography>
    ) : onSortOrderChanged ? (
        <SortableHeader
            title={title}
            sortKey={sorting.byParameter}
            currentSortKey={currentSortKey}
            onSortOrderChanged={onSortOrderChanged}
        />
    ) : (
        <UrlParamsSortableHeader title={title} sortKey={sorting.byParameter} />
    )
}

export default ColumnHeader
